 import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { handlePhoneInput, cleanPhoneNumber } from '../utils/phoneValidation';
import { getCountryOptions, getCitiesByCountry } from '../utils/countryCityData';
import NationalitySelect from '../components/NationalitySelect';
import './Tenants.css';

const Tenants = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Filtros de estado: se pueden activar ambos (Activos + Inactivos = ver todos)
  const [filterActivos, setFilterActivos] = useState(true);
  const [filterInactivos, setFilterInactivos] = useState(true);

  const { data: tenants, isLoading, error } = useQuery(
    ['tenants', search],
    async () => {
      const response = await api.get('/tenants');
      return response.data;
    },
    {
      retry: 1,
      onError: (err) => {
        if (err.code === 'ECONNABORTED' || err.message === 'Network Error') {
          toast.error('No se pudo conectar con el servidor. Verifica que el backend esté corriendo.');
        } else if (err.response?.status === 401) {
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        } else {
          toast.error(err.response?.data?.detail || 'Error al cargar tenants');
        }
      },
    }
  );

  const createTenantMutation = useMutation(
    (data) => {
      return api.post('/tenants', data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tenants');
        toast.success('Organización creada exitosamente!');
        setIsModalOpen(false);
        setEditingTenant(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          const errorMessages = errorDetail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          toast.error(`Error de validación: ${errorMessages}`);
        } else {
          toast.error(errorDetail || 'Error al crear organización');
        }
      },
    }
  );

  const updateTenantMutation = useMutation(
    ({ id, data }) => {
      return api.put(`/tenants/${id}`, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tenants');
        toast.success('Organización actualizada exitosamente!');
        setIsModalOpen(false);
        setEditingTenant(null);
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al actualizar organización');
      },
    }
  );

  const deleteTenantMutation = useMutation(
    (id) => {
      return api.delete(`/tenants/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tenants');
        toast.success('Organización eliminada exitosamente!');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al eliminar organización');
      },
    }
  );

  const toggleTenantStatusMutation = useMutation(
    ({ id, is_active }) => {
      return api.put(`/tenants/${id}`, { is_active });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tenants');
        toast.success('Estado de la organización actualizado!');
      },
      onError: (err) => {
        toast.error(err.response?.data?.detail || 'Error al cambiar estado');
      },
    }
  );

  const handleCreateClick = () => {
    setEditingTenant(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (tenant) => {
    setEditingTenant(tenant);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (tenant) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Eliminar Organización',
      message: `¿Estás seguro de eliminar la organización "${tenant.name}"? Esta acción no se puede deshacer.`,
      onConfirm: () => {
        deleteTenantMutation.mutate(tenant.id);
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
    });
  };

  const handleToggleStatus = (tenant) => {
    toggleTenantStatusMutation.mutate({ id: tenant.id, is_active: !tenant.is_active });
  };

  const handleSubmit = (formData) => {
    if (editingTenant) {
      const { code, ...rest } = formData;
      updateTenantMutation.mutate({ id: editingTenant.id, data: rest });
    } else {
      const { code, ...rest } = formData;
      createTenantMutation.mutate(rest);
    }
  };

  const filteredTenants = tenants?.filter(tenant => {
    const matchStatus = (filterActivos && tenant.is_active) || (filterInactivos && !tenant.is_active);
    if (!matchStatus) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      tenant.name?.toLowerCase().includes(searchLower) ||
      tenant.code?.toLowerCase().includes(searchLower) ||
      tenant.email?.toLowerCase().includes(searchLower) ||
      tenant.city?.toLowerCase().includes(searchLower)
    );
  }) || [];

  if (isLoading) {
    return <div className="loading">Cargando organizaciones...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>Error al cargar organizaciones</h3>
          <p>{error.response?.data?.detail || error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tenants-page">
      <div className="page-header">
        <div>
          <h1>Gestión de Organizaciones</h1>
          <p>Administra las organizaciones y academias del sistema</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreateClick}>
          + Nueva Organización
        </button>
      </div>

      <div className="filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Buscar por nombre, código, email o ciudad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <div className="status-filters">
            <button
              type="button"
              className={`filter-btn ${filterActivos && filterInactivos ? 'active' : ''}`}
              onClick={() => { setFilterActivos(true); setFilterInactivos(true); }}
            >
              Todos
            </button>
            <button
              type="button"
              className={`filter-btn ${filterActivos ? 'active' : ''}`}
              onClick={() => {
                const next = !filterActivos;
                setFilterActivos(next);
                if (!next && !filterInactivos) setFilterInactivos(true);
              }}
            >
              Activos
            </button>
            <button
              type="button"
              className={`filter-btn ${filterInactivos ? 'active' : ''}`}
              onClick={() => {
                const next = !filterInactivos;
                setFilterInactivos(next);
                if (!next && !filterActivos) setFilterActivos(true);
              }}
            >
              Inactivos
            </button>
          </div>
        </div>
      </div>

      <div className="tenants-table-container">
        <table className="tenants-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Código</th>
              <th>Email</th>
              <th>Teléfono</th>
              <th>Ciudad</th>
              <th>Disciplinas</th>
              <th>Sedes</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty-state">
                  <p>No hay organizaciones registradas</p>
                  <button className="btn btn-primary" onClick={handleCreateClick}>
                    Crear Primera Organización
                  </button>
                </td>
              </tr>
            ) : (
              filteredTenants.map((tenant) => (
                <tr key={tenant.id} className={!tenant.is_active ? 'inactive' : ''}>
                  <td>{tenant.id}</td>
                  <td>{tenant.name}</td>
                  <td>
                    <span className="code-badge">{tenant.code}</span>
                  </td>
                  <td>{tenant.email}</td>
                  <td>{tenant.phone || '-'}</td>
                  <td>{tenant.city || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>
                        {tenant.active_disciplines_count || 0}
                        {tenant.max_disciplines !== null && tenant.max_disciplines !== undefined
                          ? ` / ${tenant.max_disciplines}`
                          : ' (sin límite)'}
                      </span>
                      {tenant.max_disciplines !== null && 
                       tenant.active_disciplines_count >= tenant.max_disciplines && (
                        <small style={{ color: '#e74c3c', fontSize: '11px' }}>
                          Límite alcanzado
                        </small>
                      )}
                      {tenant.max_disciplines !== null && 
                       tenant.active_disciplines_count < tenant.max_disciplines &&
                       tenant.active_disciplines_count >= tenant.max_disciplines * 0.8 && (
                        <small style={{ color: '#f39c12', fontSize: '11px' }}>
                          Cerca del límite
                        </small>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>
                        {tenant.active_locations_count || 0}
                        {tenant.max_locations !== null && tenant.max_locations !== undefined
                          ? ` / ${tenant.max_locations}`
                          : ' (sin límite)'}
                      </span>
                      {tenant.max_locations !== null && 
                       tenant.active_locations_count >= tenant.max_locations && (
                        <small style={{ color: '#e74c3c', fontSize: '11px' }}>
                          Límite alcanzado
                        </small>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${tenant.is_active ? 'active' : 'inactive'}`}>
                      {tenant.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditClick(tenant)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleToggleStatus(tenant)}
                        title={tenant.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {tenant.is_active ? '⏸️' : '▶️'}
                      </button>
                      {tenant.code.toUpperCase() !== 'DEMO' && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteClick(tenant)}
                          title="Eliminar"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        title={editingTenant ? 'Editar Organización' : 'Nueva Organización'}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTenant(null);
        }}
        closeOnOverlayClick={false}
      >
        <TenantForm
          tenant={editingTenant}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingTenant(null);
          }}
          isLoading={createTenantMutation.isLoading || updateTenantMutation.isLoading}
        />
      </Modal>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onCancel}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      )}
    </div>
  );
};

const TenantForm = ({ tenant, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    code: tenant?.code || '',
    email: tenant?.email || '',
    phone: tenant?.phone || '',
    address: tenant?.address || '',
    city: tenant?.city || '',
    country: tenant?.country || 'España',
    max_disciplines: tenant?.max_disciplines || null,
    max_locations: tenant?.max_locations || null,
  });

  // Resetear formulario cuando cambia el tenant o se cierra
  useEffect(() => {
    if (!tenant) {
      // Nuevo tenant - resetear formulario
      setFormData({
        name: '',
        code: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: 'España',
        max_disciplines: null,
        max_locations: null,
      });
    } else {
      // Editar tenant - cargar datos
      setFormData({
        name: tenant.name || '',
        code: tenant.code || '',
        email: tenant.email || '',
        phone: tenant.phone || '',
        address: tenant.address || '',
        city: tenant.city || '',
        country: tenant.country || 'España',
        max_disciplines: tenant.max_disciplines || null,
        max_locations: tenant.max_locations || null,
      });
    }
  }, [tenant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Si es campo de teléfono, aplicar validación
    if (name === 'phone') {
      handlePhoneInput(e);
      setFormData((prev) => ({ ...prev, [name]: e.target.value }));
    } else {
      // Convertir código a mayúsculas mientras se escribe
      const processedValue = name === 'code' ? value.toUpperCase() : value;
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar campos requeridos (al crear, el código se autogenera en el backend)
    if (!formData.name || !formData.email) {
      toast.error('Por favor, completa todos los campos requeridos');
      return;
    }
    
    // Al editar no se envía code; al crear solo se envía si el usuario ingresó uno (opcional)
    const dataToSubmit = { ...formData };
    if (!tenant) {
      // Crear: no enviar code para que el backend autogenere ORG00001, ORG00002, ...
      delete dataToSubmit.code;
    }
    if (dataToSubmit.phone) {
      dataToSubmit.phone = cleanPhoneNumber(dataToSubmit.phone) || null;
    }
    
    onSubmit(dataToSubmit);
  };

  return (
    <form onSubmit={handleSubmit} className="tenant-form">
      <div className="form-group">
        <label htmlFor="name">Nombre *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        {tenant ? (
          <>
            <label htmlFor="code">Código</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              readOnly
              disabled
              style={{ textTransform: 'uppercase', backgroundColor: 'var(--bg-secondary)' }}
            />
            <small>El código no se puede modificar</small>
          </>
        ) : (
          <>
            <label>Código</label>
            <div className="form-hint" style={{ padding: '0.5rem 0', color: 'var(--text-secondary)' }}>
              Se asignará automáticamente (ej. ORG00001, ORG00002)
            </div>
          </>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Teléfono</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          disabled={isLoading}
          placeholder="+51 900 000 000"
        />
      </div>

      <div className="form-group">
        <label htmlFor="address">Dirección</label>
        <input
          type="text"
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="country">País</label>
        <NationalitySelect
          id="country"
          value={formData.country}
          disabled={isLoading}
          allowEmpty
          emptyLabel="Seleccionar país..."
          options={getCountryOptions().map((opt) => ({
            code: opt.code,
            name: opt.label,
            value: opt.value,
          }))}
          onChange={(country) => setFormData((prev) => ({ ...prev, country, city: '' }))}
        />
      </div>

      <div className="form-group">
        <label htmlFor="city">Ciudad</label>
        <select
          id="city"
          name="city"
          value={formData.city}
          onChange={handleChange}
          disabled={isLoading || !formData.country}
        >
          <option value="">{formData.country ? 'Seleccionar ciudad...' : 'Seleccione un país primero'}</option>
          {getCitiesByCountry(formData.country).map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="max_disciplines">
          Límite de Disciplinas
          <small style={{ marginLeft: '8px', color: '#666', fontWeight: 'normal' }}>
            (Dejar vacío para sin límite)
          </small>
        </label>
        <input
          type="number"
          id="max_disciplines"
          name="max_disciplines"
          value={formData.max_disciplines || ''}
          onChange={(e) => {
            const value = e.target.value;
            setFormData((prev) => ({
              ...prev,
              max_disciplines: value === '' ? null : parseInt(value) || null
            }));
          }}
          min="0"
          disabled={isLoading}
          placeholder="Sin límite"
        />
        <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
          Número máximo de disciplinas que puede crear esta organización. Si se deja vacío, no habrá límite.
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="max_locations">
          Límite de Sedes
          <small style={{ marginLeft: '8px', color: '#666', fontWeight: 'normal' }}>
            (Dejar vacío para sin límite)
          </small>
        </label>
        <input
          type="number"
          id="max_locations"
          name="max_locations"
          value={formData.max_locations || ''}
          onChange={(e) => {
            const value = e.target.value;
            setFormData((prev) => ({
              ...prev,
              max_locations: value === '' ? null : parseInt(value) || null
            }));
          }}
          min="0"
          disabled={isLoading}
          placeholder="Sin límite"
        />
        <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
          Número máximo de sedes que puede crear esta organización. Si se deja vacío, no habrá límite.
        </small>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Guardando...' : tenant ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </form>
  );
};

export default Tenants;

